import { Formik, FormikProps } from "formik";
import { Row, Col } from "antd";
import { Form, Checkbox, SubmitButton } from "formik-antd";
import { useTranslation } from "react-i18next";
import { simpleSchemaBuilder } from "/app/src/helpers";
import useSetting from "../setting";
import { useCallback, useMemo } from "react";
import { useAuthState } from "/app/src/contexts/authentication";

interface FormValues {
  value: boolean | undefined;
}

/**
 * NavigationMenus component allows users to enable or disable the redesigned navigation menus.
 * It uses Formik for form handling and integrates with the settings API.
 */
export default function NavigationMenus() {
  const { t } = useTranslation();
  const { user } = useAuthState();
  const { settingValue, createUpdateSetting } = useSetting({
    name: "navigationMenus",
    userId: user?.id,
  });

  const onSubmit = useCallback(
    async (values: FormValues, actions: FormikProps<FormValues>) => {
      await createUpdateSetting({
        ...values,
        name: "navigationMenus",
        userId: user?.id,
      }).then(() => {
        actions.resetForm();
      });
    },
    [createUpdateSetting, user?.id],
  );

  const labelContent = useMemo(() => <>{t("translation:enabled")}</>, [t]);

  const form: (props: FormikProps<FormValues>) => JSX.Element = useCallback(
    ({ isSubmitting, dirty }) => (
      <Form layout="vertical">
        <Row justify="start" gutter={16}>
          <Col span={8}>
            <h3>{t("translation:navigation_menu_title")}</h3>
            <p>{t("translation:navigation_menu_description")}</p>
          </Col>
          <Col span={13}>
            <Form.Item name="value" label={labelContent}>
              <Checkbox name="value" />
            </Form.Item>
          </Col>
          <Col span={3}>
            <SubmitButton
              className="no-label" // skipcq: JS-0394
              type="primary"
              size="large"
              block
              disabled={!dirty || isSubmitting}
            >
              {t("translation:save")}
            </SubmitButton>
          </Col>
        </Row>
      </Form>
    ),
    [labelContent, t],
  );

  return (
    <Formik
      initialValues={
        settingValue && settingValue !== "0"
          ? { value: true }
          : { value: false }
      }
      validationSchema={simpleSchemaBuilder([
        { name: "value", type: "string", required: true },
      ])}
      onSubmit={onSubmit}
      component={form}
      enableReinitialize
    />
  );
}
